/**
 * MaintenanceNotice — the full-screen notice shown while an operator has stopped new
 * workload platform-wide (OMEGA-1024 FR10, FR14).
 *
 * App.jsx selects this view AHEAD OF EVERY OTHER GUARD, so neither the Console shell nor
 * the login, register, verify-email, reset-password or card screens render while
 * maintenance is active. That ordering is the requirement, not an implementation choice:
 * with no Console signal a customer during a stop sees a working-looking interface whose
 * Submit button returns an error, which is the reading this notice exists to replace.
 *
 * What it says is the operator's own message when there is one, and the Console's own
 * default copy when there is not (FR14) — the message is optional precisely so nobody
 * has to compose under pressure, and a blank notice would be worse than a plain one.
 * The operator's text is rendered as a React text node, never through
 * `dangerouslySetInnerHTML`.
 *
 * Deliberately absent: any way to submit, any upload control, and any link into the
 * shell. The one thing a customer CAN do from here is deal with the work they already
 * have, which is MaintenanceInFlightPanel, and only when there is a session to show it
 * for (FR21).
 *
 * Accessibility, per the PRD's own section: one `<main>` landmark with one `<h1>` that
 * takes initial focus, and `role="status"` on the body so the state change is announced
 * when the notice replaces the shell mid-session rather than at a page load.
 */

import { useEffect, useRef } from "react";
import MaintenanceInFlightPanel from "./MaintenanceInFlightPanel";
import { fmtTime } from "./jobDisplay";

/**
 * FR14 — what the notice says when the operator left the message blank.
 *
 * Three constraints, all load-bearing. It states what is stopped (new workload) and what
 * is not (work already accepted), because a customer whose job is running needs that
 * answer first. It gives no end time, because there is no scheduled window and inventing
 * one would be a promise the platform cannot keep. And it carries no em dash, which the
 * backend also refuses in an operator-written message.
 */
export const DEFAULT_MAINTENANCE_MESSAGE =
  "We are carrying out maintenance, so new workloads cannot be submitted right now. " +
  "Work we have already taken on keeps running.";

export default function MaintenanceNotice({ message, since, showInFlight = false }) {
  const headingRef = useRef(null);

  // Initial focus to the heading. A keyboard or screen-reader user whose shell has just
  // been replaced is otherwise left with focus on a control that no longer exists.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const body = (typeof message === "string" && message.trim()) || DEFAULT_MAINTENANCE_MESSAGE;

  return (
    <main className="min-h-screen bg-canvas flex items-center justify-center px-4 py-8">
      <div data-testid="maintenance-notice" className="w-full max-w-lg">
        <div className="bg-ink rounded-none px-7 pt-7 pb-6">
          <div className="text-white font-semibold text-2xl tracking-[0.22em]">OMEGA AI HPC</div>
          <h1 ref={headingRef} tabIndex={-1} className="text-ink-muted text-sm mt-1 outline-none">
            Maintenance in progress
          </h1>
        </div>

        <div className="bg-surface border border-line border-t-0 rounded-none px-7 py-6 space-y-4">
          <div data-testid="maintenance-message" role="status" className="text-sm text-fg2 leading-relaxed">
            {body}
          </div>

          {since && (
            <div data-testid="maintenance-since" className="text-sm text-fg3">
              In maintenance since {fmtTime(since)}.
            </div>
          )}

          {/* No retry button: there is nothing for one to do. The Console re-checks on
              its own and returns to the normal view by itself (FR12), so a control that
              only re-asked a question already being asked every minute would invite a
              click that changes nothing. */}
          <div className="text-sm text-fg3">This page returns to the Console by itself once maintenance ends.</div>

          {showInFlight && <MaintenanceInFlightPanel />}
        </div>
      </div>
    </main>
  );
}
